import Vue from "vue";
import Editor from "@tinymce/tinymce-vue";
import VueDraggable from "vuedraggable";
import {Settings} from "luxon";
import {
    // Plugins
    BBUtil,
    Bundle,
    CvrRestApi,
    DateTimePlugin,
    DatetimeUtil,
    FormErrors,
    ParseBoolean,
    SlugUtil,
    Util,
    // Components
    BarChart,
    Barometer,
    FormattedDate,
    GlobalErrors,
    InputCallingCodeAndPhone,
    InputCustom,
    InputDate,
    InputNumber,
    // InputPhone,
    InputSecret,
    InputTags,
    InputText,
    InputTextarea,
    LanguageTabs,
    LocalErrors,
    Modal,
    SelectBooleanCheckbox,
    SelectManyCheckbox,
    SelectOneMenu,
    SelectOneRadio,
    Tree
} from "@nordiskemedier/main-components";
import AddToWatchList from "@/components/add-to-watch-list.vue";
import Banner from "@/components/banner.vue";
import Banners from "@/components/banners.vue";
import CharacterCount from "@/components/character-count.vue";
import FileCarousel from "@/components/file-carousel.vue";
import FrontPageArticle from "@/components/front-page-article.vue";
import InputFile from "@/components/input-file.vue";
import InputVideo from "@/components/input-video.vue";
import JobInFocusBox from "@/components/job-in-focus-box.vue";
import LineChart from "@/components/line-chart.vue";
import MenuLogIn from "@/components/menu-log-in.vue";
import Paginator from "@/components/paginator.vue";
import PieChart from "@/components/pie-chart.vue";
import Recaptcha from "@/components/recaptcha.vue";
import RecursiveMenu from "@/components/recursive-menu.vue";
import SelectCategory from "@/components/select-category.vue";
import SelectRegion from "@/components/select-region.vue";
import SocialButtons from "@/components/social-buttons.vue";
import Step from "@/components/step.vue";
import Steps from "@/components/steps.vue";
import Tracker from "@/components/tracker.vue";
import Types from "@/plugins/types.vue";
import Link from "@/plugins/link.vue";
import VideoPlugin from "@/plugins/video-plugin.vue";
import MemberCarrouselBox from "@/components/member-carrousel-box.vue";

import {articleService} from "@/services/article-service";
import {bordasSecureService} from "@/services/bordas-secure-service";
import {businessSecureService} from "@/services/business-secure-service";
import {categorySecureService} from "@/services/category-secure-service";
import {certificateService} from "@/services/certificate-service";
import {corporateClientSecureService} from "@/services/corporate-client-secure-service";
import {countryService} from "@/services/country-service";
import {conditionSecureService} from "@/services/condition-secure-service";
import {currencySecureService} from "@/services/currency-secure-service";
import {durationSecureService} from "@/services/duration-secure-service";
import {editionSecureService} from "@/services/edition-secure-service";
import {exchangeSecureService} from "@/services/exchange-secure-service";
import {fileService} from "@/services/file-service";
import {generatedStoryService} from "@/services/generated-story-service";
import {hireSecureService} from "@/services/hire-secure-service";
import {hostSecureService} from "@/services/host-secure-service";
import {industrySecureService} from "@/services/industry-secure-service";
import {memberContactService} from "@/services/member-contact-service";
import {memberContactSecureService} from "@/services/member-contact-secure-service";
import {memberHelpService} from "@/services/member-help-service";
import {memberInviteService} from "@/services/member-invite-service";
import {memberInviteSecureService} from "@/services/member-invite-secure-service";
import {memberLogSecureService} from "@/services/member-log-secure-service";
import {memberSecureService} from "@/services/member-secure-service";
import {memberStatsSecureService} from "@/services/member-stats-secure-service";
import {newsletterDateService} from "@/services/newsletter-date-service";
import {organizationNumberSecureService} from "@/services/organization-number-secure-service";
import {organizationNumberService} from "@/services/organization-number-service";
import {paProjectService} from "@/services/pa-project-service";
import {paUserSecureService} from "@/services/pa-user-secure-service";
import {paymentService} from "@/services/payment-service";
import {personService} from "@/services/person-service";
import {personSecureService} from "@/services/person-secure-service";
import {postNewsletterDateSecureService} from "@/services/post-newsletter-date-secure-service";
import {postService} from "@/services/post-service";
import {postSecureService} from "@/services/post-secure-service";
import {postStatsSecureService} from "@/services/post-stats-secure-service";
import {priceUnitSecureService} from "@/services/price-unit-secure-service";
import {productionUnitNumberSecureService} from "@/services/production-unit-number-secure-service";
import {regionService} from "@/services/region-service";
import {scheduleSecureService} from "@/services/schedule-secure-service";
import {subscriptionSecureService} from "@/services/subscription-secure-service";
import {supplementCategoryService} from "@/services/supplement-category-service";
import {supplementService} from "@/services/supplement-service";
import {supplementSecureService} from "@/services/supplement-secure-service";
import {watchListSecureService} from "@/services/watch-list-secure-service";
import {watchListSubscriptionSecureService} from "@/services/watch-list-subscription-secure-service";
import {watchListVatinSecureService} from "@/services/watch-list-vatin-secure-service";
import {watchNotificationTypeService} from "@/services/watch-notification-type-service";

declare global {
    interface Window {
        Bundle: any;
        contextPath: string,
        cvrRestApiAddress: string;
        logRemotely: any;
        logErrorRemotely: any;
    }
}

Vue.mixin({
    computed: {
        console: function () {
            return console
        },
        document: function () {
            return document
        },
        navigator: function () {
            return navigator
        },
        window: function () {
            return window
        }
    }
})

Settings.defaultZone = "Europe/Copenhagen";

// Only need to add services used "outside" typescript. Like in our xhtml files in native javascript Vue components.
Vue.prototype.$services = {
    articleService,
    bordasSecureService,
    businessSecureService,
    categorySecureService,
    certificateService,
    corporateClientSecureService,
    countryService,
    conditionSecureService,
    currencySecureService,
    durationSecureService,
    editionSecureService,
    exchangeSecureService,
    fileService,
    generatedStoryService,
    hireSecureService,
    hostSecureService,
    industrySecureService,
    memberContactService,
    memberContactSecureService,
    memberHelpService,
    memberInviteService,
    memberInviteSecureService,
    memberLogSecureService,
    memberSecureService,
    memberStatsSecureService,
    newsletterDateService,
    organizationNumberSecureService,
    organizationNumberService,
    personService,
    personSecureService,
    paProjectService,
    paUserSecureService,
    paymentService,
    postNewsletterDateSecureService,
    postService,
    postSecureService,
    postStatsSecureService,
    priceUnitSecureService,
    productionUnitNumberSecureService,
    regionService,
    scheduleSecureService,
    subscriptionSecureService,
    supplementCategoryService,
    supplementService,
    supplementSecureService,
    watchListSecureService,
    watchListSubscriptionSecureService,
    watchListVatinSecureService,
    watchNotificationTypeService
}

Vue.config.errorHandler = function (err, vm, info) {
    try {
        console.log(err);
        var msg;
        if (err && err.stack) {
            msg = err.stack;
        } else if (err && err.message) {
            msg = err.message;
        } else {
            msg = err;
        }
        window.logRemotely(msg + "\nInfo: " + info + "\nLocation: " + document.location + "\nBrowser: " + navigator.userAgent);
    } catch (e) {
        console.log(e);
    }
};

Vue.use(BBUtil)
Vue.use(Bundle, function (source, keys, readyFunc) { // needs an impl
    switch (source) {
        case "portal":
            window.Bundle.init(keys, function(bundle) {
                readyFunc(bundle);
            });
            break;
        default:
            throw "Unsupported source: '" + source + "'"
    }
});
Vue.use(CvrRestApi, { // needs an option
    cvrRestApiAddress: window.cvrRestApiAddress
})
Vue.use(DateTimePlugin) // used in our facelets
Vue.use(DatetimeUtil) // used in our facelets
Vue.use(FormErrors) // used in our facelets
Vue.use(ParseBoolean) // used in many places
Vue.use(SlugUtil) // used in our facelets
Vue.use(Util) // used in our facelets
Vue.use(Types) // used in our facelets
Vue.use(Link) // used in our facelets (depends on SlugUtil and Types)
Vue.use(VideoPlugin) // used in our facelets

const Components = {

    // 3rd party components
    "editor": Editor,
    "draggable": VueDraggable,

    // Main components
    "bar-chart": BarChart,
    "barometer": Barometer,
    "formatted-date": FormattedDate,
    "global-errors": GlobalErrors,
    "input-calling-code-and-phone": InputCallingCodeAndPhone,
    "input-custom": InputCustom,
    "input-date": InputDate,
    "input-number": InputNumber,
    // "input-phone": InputPhone,
    "input-secret": InputSecret,
    "input-tags": InputTags,
    "input-text": InputText,
    "input-textarea": InputTextarea,
    "language-tabs": LanguageTabs,
    "local-errors": LocalErrors,
    "modal": Modal,
    "select-boolean-checkbox": SelectBooleanCheckbox,
    "select-many-checkbox": SelectManyCheckbox,
    "select-one-menu": SelectOneMenu,
    "select-one-radio": SelectOneRadio,
    "tree": Tree,

    // Local components
    "add-to-watch-list": AddToWatchList,
    "banner": Banner,
    "banners": Banners,
    "character-count": CharacterCount,
    "file-carousel": FileCarousel,
    "front-page-article": FrontPageArticle,
    "input-file": InputFile,
    "input-video": InputVideo,
    "job-in-focus-box": JobInFocusBox,
    "menu-log-in": MenuLogIn,
    "line-chart": LineChart,
    "paginator": Paginator,
    "pie-chart": PieChart,
    "recaptcha": Recaptcha,
    "recursive-menu": RecursiveMenu,
    "select-category": SelectCategory,
    "select-region": SelectRegion,
    "social-buttons": SocialButtons,
    "step": Step,
    "steps": Steps,
    "tracker": Tracker,
    "member-carrousel-box": MemberCarrouselBox

};

Object.keys(Components).forEach(name => {
    Vue.component(name, Components[name]);
});

export default Components;
